import { useCallback, useState } from 'react';

import { useOutletContext, useSearchParams } from '@remix-run/react';

import { getOrCreateAnonymousUserId } from '~/hooks/useNectarAnalytics';
import type { OutletContext } from '~/interfaces/outlet_context';

interface StripeSessionResponse {
  session_id: string;
  url: string;
  is_duplicate: boolean;
}

const useCreateStripePriceSession = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const { cookies, cookiesSet, visitToken } = useOutletContext<OutletContext>();

  const mutateAsync = useCallback(
    async (priceId: string, email?: string, premiumOfferId?: string, isCollectingBillingAddress?: boolean) => {
      setIsLoading(true);

      try {
        const params = new URLSearchParams(searchParams);
        params.set('price_id', priceId);

        if (email) {
          params.set('email', email);
        }

        if (premiumOfferId) {
          params.set('offer_id', premiumOfferId);
        }

        if (cookiesSet && cookies.last_resource_guid && !searchParams.get('last_resource_guid')) {
          params.set('last_resource_guid', cookies.last_resource_guid);
        }

        if (isCollectingBillingAddress) {
          params.set('collect_billing_address', 'true');
        }

        params.set('nectar_upgrade_url', window.location.href);
        params.set('nectar_anonymous_user_id', getOrCreateAnonymousUserId());

        if (visitToken) {
          params.set('nectar_visit_token', visitToken);
        }

        const response = await fetch(`/stripe_session?${params}`);
        const body: StripeSessionResponse = await response.json();
        return body;
      } catch {
        // do nothing
      } finally {
        setIsLoading(false);
      }
    },
    [cookies, cookiesSet, searchParams, visitToken],
  );

  return {
    mutateAsync,
    isLoading,
  };
};

export default useCreateStripePriceSession;
